import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Shield, AlertCircle, Loader2, RefreshCcw, Trash2, Target, List, PiggyBank, Database } from 'lucide-react';
import { formatCurrency, formatDateTimeDisplay } from '../../utils/formatters';

const AdminPortal = ({ onDataChanged }) => {

  const [status, setStatus] = useState({ enabled: false });

  const [tokenInput, setTokenInput] = useState('');

  const [adminToken, setAdminToken] = useState('');

  const [overview, setOverview] = useState(null);

  const [overviewLoading, setOverviewLoading] = useState(false);

  const [overviewError, setOverviewError] = useState(null);

  const [isPerforming, setIsPerforming] = useState(false);

  const [feedback, setFeedback] = useState(null);

  const [feedbackType, setFeedbackType] = useState('success');



  useEffect(() => {

    let mounted = true;

    axios

      .get('/api/admin/status')

      .then(({ data }) => {

        if (mounted) {

          setStatus(data);

        }

      })

      .catch(() => {

        if (mounted) {

          setStatus({ enabled: false });

        }

      });

    return () => {

      mounted = false;

    };

  }, []);



  useEffect(() => {

    if (typeof window === 'undefined') return;

    const saved = window.localStorage.getItem('myfin-admin-token') || '';

    setTokenInput(saved);

    setAdminToken(saved);

  }, []);



  const fetchOverview = useCallback(async () => {

    if (!adminToken) {

      setOverview(null);

      return;

    }

    setOverviewLoading(true);

    setOverviewError(null);

    try {

      const { data } = await axios.get('/api/admin/overview', {

        headers: { 'x-admin-token': adminToken },

      });

      setOverview(data);

    } catch (err) {

      setOverview(null);

      setOverviewError(err?.response?.data?.error || 'Failed to load admin overview.');

    } finally {

      setOverviewLoading(false);

    }

  }, [adminToken]);



  useEffect(() => {

    if (!adminToken) return;

    fetchOverview();

  }, [adminToken, fetchOverview]);



  const handleSaveToken = useCallback(() => {

    const trimmed = tokenInput.trim();

    setAdminToken(trimmed);

    if (typeof window !== 'undefined') {

      if (trimmed) {

        window.localStorage.setItem('myfin-admin-token', trimmed);

      } else {

        window.localStorage.removeItem('myfin-admin-token');

      }

    }

    if (!trimmed) {

      setOverview(null);

    }

  }, [tokenInput]);



  const handleClearToken = useCallback(() => {

    setTokenInput('');

    setAdminToken('');

    if (typeof window !== 'undefined') {

      window.localStorage.removeItem('myfin-admin-token');

    }

    setOverview(null);

  }, []);



  const performAdminAction = useCallback(

    async ({ method, url, data, confirmMessage, successMessage }) => {

      if (!status?.enabled) {

        setFeedbackType('error');

        setFeedback('Admin portal is disabled on the server.');

        return;

      }

      if (!adminToken) {

        setFeedbackType('error');

        setFeedback('Enter your admin token to continue.');

        return;

      }

      if (confirmMessage && typeof window !== 'undefined' && !window.confirm(confirmMessage)) {

        return;

      }

      setIsPerforming(true);

      setFeedback(null);

      try {

        await axios({

          method,

          url,

          data,

          headers: { 'x-admin-token': adminToken },

        });

        setFeedbackType('success');

        setFeedback(successMessage || 'Action completed.');

        await fetchOverview();

        if (typeof onDataChanged === 'function') {

          onDataChanged();

        }

      } catch (err) {

        setFeedbackType('error');

        setFeedback(err?.response?.data?.error || 'Action failed.');

      } finally {

        setIsPerforming(false);

      }

    },

    [adminToken, fetchOverview, onDataChanged, status?.enabled]

  );



  const formatDateTime = (value) => {

    if (!value) return '-';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleString();

  };



  const portalDisabled = !status?.enabled;

  const actionsDisabled = portalDisabled || !adminToken || isPerforming;



  const statCards = [

    {

      label: 'Transactions',

      value: overview?.transactions?.count ?? 0,

      subLabel: overview?.transactions?.lastTransactionAt

        ? `Last activity: ${formatDateTime(overview.transactions.lastTransactionAt)}`

        : 'No transactions yet',

    },

    {

      label: 'Budgets',

      value: overview?.budgets?.count ?? 0,

      subLabel: 'Configured spending categories',

    },

    {

      label: 'Merchant Rules',

      value: overview?.merchantCategories?.count ?? 0,

      subLabel: 'Stored merchant/category overrides',

    },

    {

      label: 'Savings Goals',

      value: overview?.savingsGoals?.count ?? 0,

      subLabel: `${overview?.savingsEntries?.count ?? 0} contributions logged`,

    },

  ];



  return (

    <div className="max-w-6xl mx-auto px-4 py-10 space-y-6">

      <div className="bg-white shadow rounded-lg p-6">

        <div className="flex items-center space-x-3 mb-4">

          <div className="p-2 bg-indigo-100 rounded-full">

            <Shield className="text-indigo-600" size={24} />

          </div>

          <div>

            <h2 className="text-2xl font-semibold">Admin Portal</h2>

            <p className="text-sm text-gray-500">Danger zone tools for wiping data and refreshing defaults.</p>

          </div>

        </div>



        {portalDisabled && (

          <div className="mb-4 flex items-start space-x-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">

            <AlertCircle className="mt-0.5" size={16} />

            <span>

              Set the <code>ADMIN_TOKEN</code> environment variable on the server to enable admin actions.

            </span>

          </div>

        )}



        <div className="mb-6 grid gap-3 md:grid-cols-3">

          <div className="md:col-span-2">

            <label className="block text-sm font-medium text-gray-700 mb-1">Admin token</label>

            <input

              type="password"

              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"

              value={tokenInput}

              onChange={(e) => setTokenInput(e.target.value)}

              placeholder="Enter the ADMIN_TOKEN value"

            />

          </div>

          <div className="flex items-end space-x-2">

            <button

              onClick={handleSaveToken}

              className="flex-1 rounded-md bg-indigo-600 px-3 py-2 text-white shadow hover:bg-indigo-500 disabled:opacity-50"

              disabled={!tokenInput && !adminToken}

            >

              Save Token

            </button>

            <button

              onClick={handleClearToken}

              className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"

            >

              Clear

            </button>

          </div>

        </div>



        {feedback && (

          <div

            className={`mb-4 rounded-md px-4 py-3 text-sm ${

              feedbackType === 'error'

                ? 'bg-red-50 text-red-700 border border-red-200'

                : 'bg-green-50 text-green-700 border border-green-200'

            }`}

          >

            {feedback}

          </div>

        )}



        <div className="flex items-center justify-between mb-4">

          <div>

            <p className="text-sm font-medium text-gray-700">Overview</p>

            <p className="text-xs text-gray-500">Counts are updated whenever you run an admin action.</p>

          </div>

          <button

            onClick={fetchOverview}

            className="inline-flex items-center space-x-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"

            disabled={!adminToken || overviewLoading}

          >

            {overviewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}

            <span>{overviewLoading ? 'Refreshing...' : 'Refresh'}</span>

          </button>

        </div>



        {overviewError && (

          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">

            {overviewError}

          </div>

        )}



        <div className="grid gap-4 md:grid-cols-2">

          {statCards.map((card) => (

            <div key={card.label} className="rounded-lg border border-gray-100 bg-gray-50 p-4">

              <div className="flex items-center justify-between">

                <p className="text-sm font-medium text-gray-500">{card.label}</p>

                <Database className="text-indigo-400" size={18} />

              </div>

              <p className="mt-2 text-2xl font-semibold text-gray-900">{card.value}</p>

              <p className="text-sm text-gray-500">{card.subLabel}</p>

            </div>

          ))}

        </div>

      </div>



      <div className="grid gap-6 md:grid-cols-2">

        <div className="rounded-lg bg-white p-6 shadow">

          <div className="flex items-center space-x-3">

            <div className="rounded-full bg-red-100 p-2">

              <Trash2 className="text-red-600" size={20} />

            </div>

            <div>

              <h3 className="text-lg font-semibold">Delete all transactions</h3>

              <p className="text-sm text-gray-500">Removes transactions, splits, and their audit history.</p>

            </div>

          </div>

          <button

            onClick={() =>

              performAdminAction({

                method: 'delete',

                url: '/api/admin/transactions',

                confirmMessage: 'Delete ALL transactions, splits, and audit history? This cannot be undone.',

                successMessage: 'All transactions have been deleted.',

              })

            }

            className="mt-4 w-full rounded-md bg-red-600 px-4 py-2 text-white shadow hover:bg-red-500 disabled:opacity-50"

            disabled={actionsDisabled}

          >

            {isPerforming ? 'Working...' : 'Delete Transactions'}

          </button>

        </div>



        <div className="rounded-lg bg-white p-6 shadow">

          <div className="flex items-center space-x-3">

            <div className="rounded-full bg-indigo-100 p-2">

              <Target className="text-indigo-600" size={20} />

            </div>

            <div>

              <h3 className="text-lg font-semibold">Reset budgets</h3>

              <p className="text-sm text-gray-500">Clears custom limits and restores the default seed budgets.</p>

            </div>

          </div>

          <button

            onClick={() =>

              performAdminAction({

                method: 'post',

                url: '/api/admin/reset-budgets',

                confirmMessage: 'Reset all budgets to the default values?',

                successMessage: 'Budgets reset to defaults.',

              })

            }

            className="mt-4 w-full rounded-md bg-indigo-600 px-4 py-2 text-white shadow hover:bg-indigo-500 disabled:opacity-50"

            disabled={actionsDisabled}

          >

            {isPerforming ? 'Working...' : 'Reset Budgets'}

          </button>

        </div>



        <div className="rounded-lg bg-white p-6 shadow">

          <div className="flex items-center space-x-3">

            <div className="rounded-full bg-amber-100 p-2">

              <List className="text-amber-600" size={20} />

            </div>

            <div>

              <h3 className="text-lg font-semibold">Clear merchant memory</h3>

              <p className="text-sm text-gray-500">Removes saved merchant/category overrides.</p>

            </div>

          </div>

          <button

            onClick={() =>

              performAdminAction({

                method: 'delete',

                url: '/api/admin/merchant-categories',

                confirmMessage: 'Remove all stored merchant/category rules?',

                successMessage: 'Merchant memory cleared.',

              })

            }

            className="mt-4 w-full rounded-md bg-amber-600 px-4 py-2 text-white shadow hover:bg-amber-500 disabled:opacity-50"

            disabled={actionsDisabled}

          >

            {isPerforming ? 'Working...' : 'Clear Merchant Rules'}

          </button>

        </div>



        <div className="rounded-lg bg-white p-6 shadow">

          <div className="flex items-center space-x-3">

            <div className="rounded-full bg-teal-100 p-2">

              <PiggyBank className="text-teal-600" size={20} />

            </div>

            <div>

              <h3 className="text-lg font-semibold">Delete savings data</h3>

              <p className="text-sm text-gray-500">Clears all savings goals and contribution history.</p>

            </div>

          </div>

          <button

            onClick={() =>

              performAdminAction({

                method: 'delete',

                url: '/api/admin/savings',

                confirmMessage: 'Delete all savings goals and contributions?',

                successMessage: 'Savings data deleted.',

              })

            }

            className="mt-4 w-full rounded-md bg-teal-600 px-4 py-2 text-white shadow hover:bg-teal-500 disabled:opacity-50"

            disabled={actionsDisabled}

          >

            {isPerforming ? 'Working...' : 'Delete Savings Data'}

          </button>

        </div>

      </div>

    </div>

  );

};



/**

 * Main App Component

 */


export default AdminPortal;
